import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingCart, User, LogOut, Package, LayoutDashboard, Search, X, Minus, Plus, MessageCircle, CreditCard, QrCode, Menu, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import api, { brl } from "@/lib/api";
import { Thumb } from "@/components/ui-kit";
import BrandLogo from "@/components/brand/BrandLogo";

function CartDrawer() {
  const { items, updateQty, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} data-testid="cart-overlay" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white border-l border-slate-200 flex flex-col fade-up" data-testid="cart-drawer">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Carrinho ({items.length})</h3>
          <button onClick={() => setOpen(false)} data-testid="cart-close-btn" className="p-1 hover:text-brand-600"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4 scrollbar-thin">
          {items.length === 0 && <p className="text-slate-500 text-sm" data-testid="cart-empty-msg">Seu carrinho está vazio.</p>}
          {items.map((i) => (
            <div key={`${i.product_id}:${i.variant_id}`} className="flex gap-3 border border-slate-200 rounded-lg p-3" data-testid="cart-item">
              <Thumb src={i.image} alt={i.name} iconSize={18} className="w-16 h-16 object-cover rounded-md shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{i.name}</p>
                <p className="text-xs text-slate-500">{i.variant_label}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2 border border-slate-200 rounded-md px-1">
                    <button onClick={() => updateQty(i.product_id, i.variant_id, i.qty - 1)} data-testid="cart-item-decrease" className="p-1"><Minus size={14} /></button>
                    <span className="text-sm w-5 text-center" data-testid="cart-item-qty">{i.qty}</span>
                    <button onClick={() => updateQty(i.product_id, i.variant_id, i.qty + 1)} data-testid="cart-item-increase" className="p-1"><Plus size={14} /></button>
                  </div>
                  <span className="text-sm font-semibold font-mono">{brl(i.price * i.qty)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-5 border-t border-slate-200 space-y-3">
          <div className="flex justify-between text-slate-900 font-semibold">
            <span>Subtotal</span><span className="font-mono" data-testid="cart-subtotal">{brl(subtotal)}</span>
          </div>
          <button
            disabled={items.length === 0}
            onClick={() => { setOpen(false); navigate("/checkout"); }}
            data-testid="cart-checkout-btn"
            className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-40 text-white font-semibold py-3 rounded-lg transition-colors"
          >Finalizar compra</button>
        </div>
      </aside>
    </div>
  );
}

/**
 * Navegação da loja no celular.
 *
 * Até aqui a busca e a barra de categorias eram `hidden md:` — escondidas
 * abaixo de 768px e sem substituto nenhum. No celular o cabeçalho tinha logo,
 * conta e carrinho, e nada mais: para chegar a "Andadores" o cliente rolava a
 * home inteira até os cartões de categoria, a 750px de rolagem. Numa loja de
 * catálogo, busca É a navegação principal, e a maior parte do tráfego é
 * celular.
 *
 * A gaveta entra por cima com a BUSCA PRIMEIRO e o foco nela: quem abre o menu
 * de uma loja quase sempre está procurando um produto, não lendo o índice.
 * Depois as categorias, e por último a conta — inclusive os atalhos de admin e
 * PDV, que saem do cabeçalho e param de disputar os 390px com o carrinho.
 */
function MobileNav({ open, onClose, categories, user, onLogout }) {
  const navigate = useNavigate();
  const [termo, setTermo] = useState("");

  // Rolagem do fundo travada: sem isso a página corre atrás da gaveta e a
  // pessoa perde o lugar onde estava ao fechar.
  useEffect(() => {
    if (!open) return;
    const antes = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);

    return () => { document.body.style.overflow = antes; window.removeEventListener("keydown", onKey); };
  }, [open, onClose]);

  if (!open) return null;

  const ir = (to) => { onClose(); navigate(to); };
  const buscar = (e) => {
    e.preventDefault();
    const q = termo.trim();
    onClose();
    navigate(q ? `/produtos?busca=${encodeURIComponent(q)}` : "/produtos");
  };

  return (
    <div className="fixed inset-0 z-50 md:hidden" data-testid="store-mobile-nav">
      {/* `button` e não `div` com onClick: fechar pelo fundo precisa ser
          alcançável pelo teclado como qualquer outro controle. */}
      <button type="button" aria-label="Fechar menu" onClick={onClose}
        data-testid="store-mobile-nav-overlay" className="absolute inset-0 bg-slate-900/40 cursor-default" />
      <aside className="absolute left-0 top-0 h-full w-[88%] max-w-sm bg-white flex flex-col shadow-xl">
        <div className="flex items-center justify-between px-4 h-[60px] border-b border-slate-200 shrink-0">
          <BrandLogo size={34} typeHeight={19} />
          {/* 44px de alvo: é o fechar de uma gaveta operada com o polegar. */}
          <button onClick={onClose} aria-label="Fechar menu" data-testid="store-mobile-nav-close"
            className="h-11 w-11 -mr-2 inline-flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={buscar} className="p-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center border border-slate-300 rounded-full pl-4 pr-1 py-1 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100">
            {/* `text-base` e não `text-sm`: abaixo de 16px o Safari do iPhone
                amplia a tela ao focar o campo e não volta sozinho. */}
            <input autoFocus value={termo} onChange={(e) => setTermo(e.target.value)}
              aria-label="Buscar produtos na loja" placeholder="Estou procurando por..."
              data-testid="store-mobile-search-input"
              className="flex-1 py-2 text-base outline-none bg-transparent" />
            <button type="submit" aria-label="Buscar" data-testid="store-mobile-search-btn"
              className="bg-brand-600 hover:bg-brand-700 text-white rounded-full h-9 w-9 inline-flex items-center justify-center shrink-0">
              <Search size={16} />
            </button>
          </div>
        </form>

        <nav className="flex-1 overflow-y-auto scrollbar-thin py-2">
          <Item onClick={() => ir("/produtos")} testId="store-mobile-all-products" forte>Todos os Produtos</Item>
          {categories.map((c) => (
            <Item key={c.slug} onClick={() => ir(`/produtos?categoria=${c.slug}`)} testId={`store-mobile-category-${c.slug}`}>
              {c.name}
            </Item>
          ))}
          <div className="my-2 border-t border-slate-100" />
          <Item onClick={() => ir("/sobre")} testId="store-mobile-about">Sobre Nós</Item>

          {/* Conta e atalhos de operação no fim: saíram do cabeçalho, que em
              390px não cabia o carrinho com eles. */}
          <div className="my-2 border-t border-slate-100" />
          {user?.role === "admin" && <Item onClick={() => ir("/admin")} testId="store-mobile-admin" icone={LayoutDashboard}>Painel administrativo</Item>}
          {user?.role === "vendedor" && <Item onClick={() => ir("/vendedor")} testId="store-mobile-pdv" icone={LayoutDashboard}>PDV</Item>}
          {user ? (
            <>
              <Item onClick={() => ir("/conta/pedidos")} testId="store-mobile-orders" icone={Package}>Meus pedidos</Item>
              <Item onClick={() => { onClose(); onLogout(); navigate("/"); }} testId="store-mobile-logout" icone={LogOut}>Sair</Item>
            </>
          ) : (
            <Item onClick={() => ir("/login")} testId="store-mobile-login" icone={User}>Entrar / Cadastrar</Item>
          )}
        </nav>
      </aside>
    </div>
  );
}

/** Linha da gaveta: 48px de altura, que é alvo de toque com folga. */
function Item({ children, onClick, testId, icone: Icone, forte = false }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`w-full flex items-center gap-3 px-4 h-12 text-left text-[15px] hover:bg-slate-50 transition-colors ${forte ? "font-semibold text-slate-900" : "text-slate-700"}`}>
      {Icone ? <Icone size={18} className="text-slate-400 shrink-0" aria-hidden /> : <span className="w-[18px] shrink-0" aria-hidden />}
      <span className="flex-1 min-w-0 truncate">{children}</span>
      <ChevronRight size={16} className="text-slate-300 shrink-0" aria-hidden />
    </button>
  );
}

export default function StoreLayout() {
  const { user, logout } = useAuth();
  const { items, count, setOpen } = useCart();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState(null);
  const [menuAberto, setMenuAberto] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { api.post("/cart/sync", { items }).catch(() => {}); }, 1200);
    return () => clearTimeout(t);
  }, [items, user]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/produtos?busca=${encodeURIComponent(search)}`);
  };

  const whatsapp = settings?.whatsapp || "5567998098308";

  return (
    <div className="min-h-screen flex flex-col">
      {/* barra promocional */}
      <div className="bg-[#0B2038] text-white text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-5 overflow-hidden whitespace-nowrap">
            <span className="flex items-center gap-1.5"><CreditCard size={13} className="text-brand-300" /> 12x sem juros no cartão</span>
            <span className="hidden sm:flex items-center gap-1.5"><QrCode size={13} className="text-brand-300" /> 5% OFF no PIX</span>
            {/* A frase muda com a regra: só dá para prometer "acima de X" para o
                site inteiro quando toda categoria participa com o mesmo mínimo.
                Havendo exceção, o número vira "a partir de" e o texto diz que
                nem tudo entra — anunciar o contrário é prometer o que a loja
                não vai cumprir no checkout. */}
            {settings?.free_shipping_min > 0 && (
              <span className="hidden md:inline text-slate-300">
                {settings?.free_shipping_uniform === false
                  ? `Frete grátis a partir de ${brl(settings.free_shipping_min)} em categorias participantes`
                  : `Frete grátis acima de ${brl(settings.free_shipping_min)}`}
              </span>
            )}
          </div>
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" data-testid="topbar-whatsapp"
            className="flex items-center gap-1.5 hover:text-brand-300 transition-colors shrink-0">
            <MessageCircle size={13} className="text-emerald-400" /> (67) 9 9809-8308
          </a>
        </div>
      </div>

      {/* header principal */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-3 sm:gap-4 lg:gap-8">
          {/* O hambúrguer é a porta da busca e das categorias no celular, que
              antes simplesmente não existiam abaixo de 768px. */}
          <button onClick={() => setMenuAberto(true)} aria-label="Abrir menu da loja"
            data-testid="nav-mobile-menu-btn"
            className="md:hidden h-11 w-11 -ml-2 inline-flex items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100 shrink-0">
            <Menu size={22} />
          </button>
          <Link to="/" data-testid="nav-logo" className="shrink-0">
            <BrandLogo size={42} typeHeight={24} />
          </Link>
          <form onSubmit={submitSearch} className="flex-1 max-w-xl hidden md:flex items-center border border-slate-300 rounded-full pl-4 pr-1 py-1 bg-white focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100 transition-all">
            <input value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Buscar produtos na loja" placeholder="Estou procurando por..."
              data-testid="nav-search-input" className="flex-1 py-1.5 text-sm outline-none bg-transparent" />
            <button type="submit" data-testid="nav-search-btn" className="bg-brand-600 hover:bg-brand-700 text-white rounded-full p-2 transition-colors"><Search size={15} /></button>
          </form>
          {/* Abaixo de `md` sobram logo, hambúrguer e carrinho. Com os atalhos
              de operação aqui, os 390px do iPhone não davam conta e o carrinho
              saía cortado pela borda — a página inteira ganhava 19px de
              rolagem horizontal. */}
          <nav className="ml-auto flex items-center gap-1 sm:gap-2 [&>*:not(:last-child)]:hidden md:[&>*:not(:last-child)]:flex">
            {/* O ÍCONE aparece sempre; só o rótulo é que espera caber.
                Com `hidden sm:flex` no link inteiro, quem administrava a loja
                pelo celular ficava sem porta de entrada para o painel: sobrava
                "Meus Pedidos" e o botão de sair, e o caminho era digitar /admin
                na barra de endereço. */}
            {user?.role === "admin" && <Link to="/admin" data-testid="nav-admin-link" aria-label="Abrir o painel administrativo" className="text-sm font-medium text-slate-700 hover:text-brand-600 px-2 flex items-center gap-1"><LayoutDashboard size={19} className="sm:w-4 sm:h-4" /><span className="hidden sm:inline">Admin</span></Link>}
            {user?.role === "vendedor" && <Link to="/vendedor" data-testid="nav-seller-link" className="text-sm font-medium text-slate-700 hover:text-brand-600 px-2">PDV</Link>}
            {user ? (
              <>
                <Link to="/conta/pedidos" data-testid="nav-orders-link" className="flex items-center gap-2 px-2 text-slate-700 hover:text-brand-600">
                  <Package size={19} />
                  <span className="hidden lg:block text-left leading-tight"><span className="block text-[11px] text-slate-400">Meus</span><span className="block text-xs font-semibold -mt-0.5">Pedidos</span></span>
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn" className="text-slate-400 hover:text-red-500 px-2"><LogOut size={17} /></button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login-link" className="flex items-center gap-2 px-2 text-slate-700 hover:text-brand-600">
                <User size={19} />
                <span className="hidden lg:block text-left leading-tight"><span className="block text-[11px] text-slate-400">Entrar /</span><span className="block text-xs font-semibold -mt-0.5">Cadastrar</span></span>
              </Link>
            )}
            <button onClick={() => setOpen(true)} data-testid="nav-cart-btn" className="relative flex items-center gap-2 pl-2 pr-1 text-slate-700 hover:text-brand-600">
              <span className="relative">
                <ShoppingCart size={21} />
                {count > 0 && <span data-testid="nav-cart-count" className="absolute -top-1.5 -right-1.5 bg-brand-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{count}</span>}
              </span>
              <span className="hidden lg:block text-left leading-tight"><span className="block text-[11px] text-slate-400">{count} {count === 1 ? "item" : "itens"}</span><span className="block text-xs font-semibold -mt-0.5">Carrinho</span></span>
            </button>
          </nav>
        </div>

        {/* nav de categorias */}
        <nav className="bg-brand-600 text-white hidden md:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-1 overflow-x-auto scrollbar-thin">
            <NavLink to="/produtos" end data-testid="nav-products-link"
              className="text-[13px] font-semibold px-3.5 py-2.5 hover:bg-white/10 whitespace-nowrap transition-colors">
              Todos os Produtos
            </NavLink>
            {categories.map((c) => (
              <Link key={c.slug} to={`/produtos?categoria=${c.slug}`} data-testid={`nav-category-${c.slug}`}
                className="text-[13px] font-medium px-3.5 py-2.5 hover:bg-white/10 whitespace-nowrap transition-colors">
                {c.name}
              </Link>
            ))}
            <NavLink to="/sobre" data-testid="nav-about-link"
              className={({ isActive }) => `text-[13px] font-medium px-3.5 py-2.5 whitespace-nowrap transition-colors ml-auto ${isActive ? "bg-white/15" : "hover:bg-white/10"}`}>
              Sobre Nós
            </NavLink>
          </div>
        </nav>
      </header>

      <MobileNav
        open={menuAberto}
        onClose={() => setMenuAberto(false)}
        categories={categories}
        user={user}
        onLogout={logout}
      />

      <main className="flex-1"><Outlet /></main>

      {/* footer */}
      <footer className="bg-[#0B2038] text-slate-300 mt-16">
        <div className="max-w-7xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-sm">
          <div className="col-span-2 md:col-span-1">
            <BrandLogo size={40} typeHeight={22} light className="mb-4" />
            <p className="text-slate-400 leading-relaxed">Produtos ortopédicos com qualidade hospitalar. Loja física + online, um só estoque.</p>
            <p className="mt-3 text-xs text-brand-300 font-semibold tracking-wide">CUIDAR · REABILITAR · TRANSFORMAR</p>
          </div>
          <div>
            <p className="font-semibold text-white mb-3">Institucional</p>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/sobre" className="hover:text-white">Sobre nós</Link></li>
              <li><Link to="/produtos" className="hover:text-white">Nossos produtos</Link></li>
              <li><Link to="/cadastro" className="hover:text-white">Criar conta</Link></li>
              <li><Link to="/login" className="hover:text-white">Área do cliente</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white mb-3">Ajuda</p>
            <ul className="space-y-2 text-slate-400">
              <li><Link to="/conta/pedidos" className="hover:text-white">Rastrear pedido</Link></li>
              <li><Link to="/conta/notas" className="hover:text-white">2ª via de nota fiscal</Link></li>
              <li><a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-white">Trocas e devoluções</a></li>
            </ul>
          </div>
          <div>
            <p className="font-semibold text-white mb-3">Atendimento</p>
            <ul className="space-y-2 text-slate-400">
              <li>Seg a Sex — 8h às 18h</li>
              <li><a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-white flex items-center gap-1.5"><MessageCircle size={14} className="text-emerald-400" /> (67) 9 9809-8308</a></li>
              <li>contato@ortopedicasaopaulo.com.br</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-500">
            <span>© {new Date().getFullYear()} Ortopédica São Paulo · CNPJ 00.000.000/0001-00</span>
            <span>PIX · Cartão em até 12x · Boleto — Compra 100% segura</span>
          </div>
        </div>
      </footer>

      {/* WhatsApp flutuante */}
      <a href={`https://wa.me/${whatsapp}?text=Olá! Vim pelo site da Ortopédica São Paulo.`} target="_blank" rel="noreferrer"
        data-testid="whatsapp-float" aria-label="Falar no WhatsApp"
        className="fixed bottom-5 right-5 z-40 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full p-3.5 shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105">
        <MessageCircle size={24} />
      </a>

      <CartDrawer />
    </div>
  );
}
